/**
 * Eyes Test ToM — corpus textuel MVP.
 *
 * MVP corpus textuel, photos eyes corpus (RMET Baron-Cohen 2001) arrive Sprint 2+.
 *
 * Source paradigme : Reading the Mind in the Eyes Test (Baron-Cohen, Wheelwright,
 * Hill, Raste & Plumb 2001, JCPP 42(2):241-251, doi:10.1111/1469-7610.00715).
 *
 * Le corpus original RMET = 36 photos N&B (yeux + sourcils) + 4 mots/item.
 * Ici on remplace l'image par un descripteur textuel (placeholder visuel)
 * + emoji 👁️ 👁️ pour la cohérence visuelle MVP.
 *
 * `difficulty` 1..5 = tier de proximité sémantique des distracteurs :
 *   1 = distracteurs nets (très différents de la cible)
 *   3 = distracteurs plausibles
 *   5 = quasi-synonymes, vraie subtilité
 *
 * Les libellés des choix (FR) sont hardcodés ici pour le MVP.
 * TODO Sprint 2+ : i18n corpus eyes (n8n batch traduction + revue native).
 */
export const EYES_CORPUS = [
    // ============================================================
    // Difficulty 1 — distracteurs nets (6 items)
    // ============================================================
    {
        id: 'rmet-fr-001',
        descriptor: 'Regard fixe, sourcils relevés haut, paupières grandes ouvertes',
        emoji: '👁️ 👁️',
        choices: ['surpris', 'fatigué', 'colérique', 'serein'],
        correctIdx: 0,
        difficulty: 1,
        indices: 'sourcils relevés et paupières grandes ouvertes signalent surprise',
    },
    {
        id: 'rmet-fr-002',
        descriptor: 'Paupières mi-closes, regard tombant, peu de tension',
        emoji: '👁️ 👁️',
        choices: ['amusé', 'fatigué', 'inquiet', 'fier'],
        correctIdx: 1,
        difficulty: 1,
        indices: 'paupières mi-closes et tension faible évoquent la fatigue',
    },
    {
        id: 'rmet-fr-003',
        descriptor: 'Sourcils froncés et rapprochés, regard intense vers la cible',
        emoji: '👁️ 👁️',
        choices: ['serein', 'amusé', 'colérique', 'pensif'],
        correctIdx: 2,
        difficulty: 1,
        indices: 'sourcils froncés rapprochés + intensité = colère',
    },
    {
        id: 'rmet-fr-004',
        descriptor: 'Pattes d\'oie marquées, paupières inférieures relevées',
        emoji: '👁️ 👁️',
        choices: ['triste', 'méfiant', 'amusé', 'lassé'],
        correctIdx: 2,
        difficulty: 1,
        indices: 'pattes d\'oie + paupières inférieures relevées trahissent un sourire véritable',
    },
    {
        id: 'rmet-fr-005',
        descriptor: 'Sourcils en accent circonflexe, regard humide légèrement baissé',
        emoji: '👁️ 👁️',
        choices: ['triste', 'fier', 'concentré', 'confiant'],
        correctIdx: 0,
        difficulty: 1,
        indices: 'sourcils en accent circonflexe + humidité signalent la tristesse',
    },
    {
        id: 'rmet-fr-006',
        descriptor: 'Paupières détendues, regard direct, micro-tension neutre',
        emoji: '👁️ 👁️',
        choices: ['serein', 'colérique', 'surpris', 'coupable'],
        correctIdx: 0,
        difficulty: 1,
        indices: 'absence de tension + regard direct signe la sérénité',
    },
    // ============================================================
    // Difficulty 2 — distracteurs proches (6 items)
    // ============================================================
    {
        id: 'rmet-fr-101',
        descriptor: 'Sourcil gauche relevé, droit immobile, micro-sourire des yeux',
        emoji: '👁️ 👁️',
        choices: ['sceptique', 'surpris', 'pensif', 'amusé'],
        correctIdx: 0,
        difficulty: 2,
        indices: 'asymétrie sourcils + micro-sourire = scepticisme contenu',
    },
    {
        id: 'rmet-fr-102',
        descriptor: 'Regard légèrement de côté, paupières mi-closes, sourcils détendus',
        emoji: '👁️ 👁️',
        choices: ['concentré', 'pensif', 'lassé', 'inquiet'],
        correctIdx: 1,
        difficulty: 2,
        indices: 'regard de côté + détente = ailleurs mentalement, pensif',
    },
    {
        id: 'rmet-fr-103',
        descriptor: 'Paupières plissées, regard scrutateur dirigé vers une cible',
        emoji: '👁️ 👁️',
        choices: ['amusé', 'concentré', 'méfiant', 'serein'],
        correctIdx: 1,
        difficulty: 2,
        indices: 'plissement + scrutation = focus attentionnel',
    },
    {
        id: 'rmet-fr-104',
        descriptor: 'Sourcils légèrement abaissés, regard latéral, paupières tendues',
        emoji: '👁️ 👁️',
        choices: ['méfiant', 'fatigué', 'amusé', 'fier'],
        correctIdx: 0,
        difficulty: 2,
        indices: 'sourcils abaissés + regard latéral = méfiance',
    },
    {
        id: 'rmet-fr-105',
        descriptor: 'Yeux brillants, micro-sourire, paupières inférieures soulevées',
        emoji: '👁️ 👁️',
        choices: ['fier', 'amusé', 'triste', 'surpris'],
        correctIdx: 1,
        difficulty: 2,
        indices: 'brillance + paupières inférieures soulevées = amusement sincère',
    },
    {
        id: 'rmet-fr-106',
        descriptor: 'Sourcils relevés au milieu, regard interrogatif vers le haut',
        emoji: '👁️ 👁️',
        choices: ['inquiet', 'pensif', 'concentré', 'amusé'],
        correctIdx: 0,
        difficulty: 2,
        indices: 'sourcils centrés relevés + regard vers le haut = inquiétude diffuse',
    },
    // ============================================================
    // Difficulty 3 — distracteurs plausibles (6 items)
    // ============================================================
    {
        id: 'rmet-fr-201',
        descriptor: 'Œil gauche très légèrement plissé, regard direct, lèvres invisibles',
        emoji: '👁️ 👁️',
        choices: ['malicieux', 'sceptique', 'amusé', 'concentré'],
        correctIdx: 0,
        difficulty: 3,
        indices: 'plissement asymétrique léger = malice contenue',
    },
    {
        id: 'rmet-fr-202',
        descriptor: 'Paupières neutres, regard fixe vers un point lointain, mâchoire détendue',
        emoji: '👁️ 👁️',
        choices: ['pensif', 'lassé', 'serein', 'concentré'],
        correctIdx: 0,
        difficulty: 3,
        indices: 'fixation lointaine + détente = pensif (rumination douce)',
    },
    {
        id: 'rmet-fr-203',
        descriptor: 'Regard dirigé vers le bas et de côté, sourcils légèrement abaissés',
        emoji: '👁️ 👁️',
        choices: ['triste', 'coupable', 'fatigué', 'concentré'],
        correctIdx: 1,
        difficulty: 3,
        indices: 'regard fuyant bas-côté = culpabilité (évitement social)',
    },
    {
        id: 'rmet-fr-204',
        descriptor: 'Sourcils légèrement froncés, regard vers une cible, paupières actives',
        emoji: '👁️ 👁️',
        choices: ['déterminé', 'colérique', 'concentré', 'méfiant'],
        correctIdx: 0,
        difficulty: 3,
        indices: 'froncement modéré + cible = détermination, pas colère',
    },
    {
        id: 'rmet-fr-205',
        descriptor: 'Paupières mi-relevées, regard direct, micro-tension menton',
        emoji: '👁️ 👁️',
        choices: ['confiant', 'fier', 'serein', 'concentré'],
        correctIdx: 0,
        difficulty: 3,
        indices: 'paupières ouvertes posées + tension légère = confiance',
    },
    {
        id: 'rmet-fr-206',
        descriptor: 'Sourcils légèrement relevés, regard chaleureux, paupières souples',
        emoji: '👁️ 👁️',
        choices: ['bienveillant', 'amusé', 'serein', 'fier'],
        correctIdx: 0,
        difficulty: 3,
        indices: 'relevage doux + chaleur = bienveillance ouverte',
    },
    // ============================================================
    // Difficulty 4 — distracteurs très proches (6 items)
    // ============================================================
    {
        id: 'rmet-fr-301',
        descriptor: 'Regard légèrement plissé, lueur dans l\'œil, sourcil unique haussé',
        emoji: '👁️ 👁️',
        choices: ['malicieux', 'amusé', 'fier', 'sceptique'],
        correctIdx: 0,
        difficulty: 4,
        indices: 'lueur + sourcil haussé asymétrique = malice (vs amusement franc)',
    },
    {
        id: 'rmet-fr-302',
        descriptor: 'Sourcils figés, paupières immobiles, regard très fixe',
        emoji: '👁️ 👁️',
        choices: ['déterminé', 'concentré', 'méfiant', 'colérique'],
        correctIdx: 0,
        difficulty: 4,
        indices: 'fixité totale + figement = détermination (vs concentration plus mobile)',
    },
    {
        id: 'rmet-fr-303',
        descriptor: 'Paupières mi-tombantes, regard vide, sourcils sans tension',
        emoji: '👁️ 👁️',
        choices: ['lassé', 'fatigué', 'pensif', 'triste'],
        correctIdx: 0,
        difficulty: 4,
        indices: 'vide + absence de tension = lassitude (vs fatigue physique)',
    },
    {
        id: 'rmet-fr-304',
        descriptor: 'Sourcils centraux relevés discrètement, regard dirigé vers l\'autre',
        emoji: '👁️ 👁️',
        choices: ['bienveillant', 'inquiet', 'pensif', 'serein'],
        correctIdx: 0,
        difficulty: 4,
        indices: 'sourcils centraux relevés + orientation altruiste = bienveillance attentive',
    },
    {
        id: 'rmet-fr-305',
        descriptor: 'Regard dirigé vers le bas, paupières inférieures contractées',
        emoji: '👁️ 👁️',
        choices: ['coupable', 'triste', 'pensif', 'lassé'],
        correctIdx: 0,
        difficulty: 4,
        indices: 'baisser le regard + contraction = culpabilité (vs tristesse plus douce)',
    },
    {
        id: 'rmet-fr-306',
        descriptor: 'Paupières inférieures soulevées symétriques, regard chaleureux fixe',
        emoji: '👁️ 👁️',
        choices: ['amusé', 'bienveillant', 'fier', 'confiant'],
        correctIdx: 0,
        difficulty: 4,
        indices: 'soulèvement symétrique + chaleur = amusement (rire des yeux)',
    },
    // ============================================================
    // Difficulty 5 — quasi-synonymes (6 items)
    // ============================================================
    {
        id: 'rmet-fr-401',
        descriptor: 'Sourcil unique très légèrement haussé, regard scrutateur silencieux',
        emoji: '👁️ 👁️',
        choices: ['sceptique', 'méfiant', 'pensif', 'concentré'],
        correctIdx: 0,
        difficulty: 5,
        indices: 'haussement minimal + scrutation = scepticisme (méfiance serait plus tendue)',
    },
    {
        id: 'rmet-fr-402',
        descriptor: 'Regard chaleureux dirigé vers l\'autre, paupières inférieures détendues',
        emoji: '👁️ 👁️',
        choices: ['bienveillant', 'serein', 'amusé', 'confiant'],
        correctIdx: 0,
        difficulty: 5,
        indices: 'chaleur + orientation autrui = bienveillance (sérénité serait plus neutre)',
    },
    {
        id: 'rmet-fr-403',
        descriptor: 'Paupières immobiles, regard cible, micro-tension périoculaire',
        emoji: '👁️ 👁️',
        choices: ['déterminé', 'concentré', 'fier', 'confiant'],
        correctIdx: 0,
        difficulty: 5,
        indices: 'tension périoculaire + cible = détermination (concentration moins tendue)',
    },
    {
        id: 'rmet-fr-404',
        descriptor: 'Regard vers le haut-côté, sourcils légèrement abaissés au centre',
        emoji: '👁️ 👁️',
        choices: ['pensif', 'lassé', 'inquiet', 'sceptique'],
        correctIdx: 0,
        difficulty: 5,
        indices: 'orientation haut-côté + abaissement central = pensif (introspection)',
    },
    {
        id: 'rmet-fr-405',
        descriptor: 'Paupières inférieures relevées, lueur sous-jacente, sourcils discrets',
        emoji: '👁️ 👁️',
        choices: ['malicieux', 'amusé', 'fier', 'confiant'],
        correctIdx: 0,
        difficulty: 5,
        indices: 'lueur sous paupière + discrétion = malice (amusement serait plus ouvert)',
    },
    {
        id: 'rmet-fr-406',
        descriptor: 'Regard direct, paupières inférieures fermes, micro-élévation menton',
        emoji: '👁️ 👁️',
        choices: ['fier', 'confiant', 'déterminé', 'serein'],
        correctIdx: 0,
        difficulty: 5,
        indices: 'menton relevé + fermeté = fierté (confiance serait plus posée)',
    },
];
/** Filtre items par tier de difficulté. */
export function itemsByDifficulty(difficulty) {
    const clamped = Math.max(1, Math.min(5, Math.round(difficulty)));
    return EYES_CORPUS.filter((i) => i.difficulty === clamped);
}
//# sourceMappingURL=corpus.js.map